import axios from 'axios';


const api = axios.create({

  baseURL: window.location.origin + '/api', // Dynamically set base URL

  headers: { 'Content-Type': 'multipart/form-data' }

});


// Add extensive logging

api.interceptors.request.use(

  (config) => {

    console.log('API Request:', {

      url: config.url,

      method: config.method,

      baseURL: config.baseURL

    });

    return config;

  },

  (error) => {

    console.error('Request Error:', error);

    return Promise.reject(error);

  }

);


api.interceptors.response.use(

  (response) => response,

  (error) => {

    console.error('Full API Error:', {

      message: error.message,

      code: error.code,

      url: error.config?.url,

      method: error.config?.method

    });

    return Promise.reject(error);

  }

);


// Image Analysis API Calls

export const analyzeImage = {

  detectLabels: async (imageFile) => {

    try {

      const formData = new FormData();

      formData.append("image", imageFile);

      return await api.post("/image/labels", formData);

    } catch (error) {

      console.error("Error detecting labels:", error);

      throw error;

    }

  },


  detectFaces: async (imageFile) => {

    try {

      const formData = new FormData();

      formData.append("image", imageFile);

      return await api.post("/image/faces", formData);

    } catch (error) {

      console.error("Error detecting faces:", error);

      throw error;

    }

  },


  detectText: async (imageFile) => {

    try {

      const formData = new FormData();

      formData.append("image", imageFile);

      return await api.post("/image/text", formData);

    } catch (error) {

      console.error("Error detecting text:", error);

      throw error;

    }

  },


  detectModeration: async (imageFile) => {

    try {

      const formData = new FormData();

      formData.append("image", imageFile);

      return await api.post("/image/moderation", formData);

    } catch (error) {

      console.error("Error detecting moderation:", error);

      throw error;

    }

  },

};


// Face Comparison API Calls

export const faceAPI = {

  compareFaces: async (sourceImage, targetImage, similarityThreshold = 70) => {

    try {

      const formData = new FormData();

      formData.append("source", sourceImage);

      formData.append("target", targetImage);

      formData.append("similarityThreshold", similarityThreshold);

      return await api.post("/face/compare", formData);

    } catch (error) {

      console.error("Error comparing faces:", error);

      throw error;

    }

  },


  searchFaces: async (
    imageFile,
    collectionId,
    maxFaces = 5,
    faceMatchThreshold = 70,
    { signal } = {}
  ) => {

    try {

      const formData = new FormData();

      formData.append("image", imageFile);

      formData.append("collectionId", collectionId);

      formData.append("maxFaces", maxFaces);

      formData.append("faceMatchThreshold", faceMatchThreshold);

      return await api.post("/face/search", formData, {
        signal,
        timeout: 120000
      });

    } catch (error) {

      console.error("Error searching faces:", error);

      throw error;

    }

  },


  listCollections: async () => {

    try {

      return await api.get("/face/collections");

    } catch (error) {

      console.error("Error listing collections:", error);

      throw error;

    }

  },

};

// Video Analysis API Calls

export const videoAPI = {

  startLabelDetection: async (videoFile, minConfidence = 70) => {

    try {

      const formData = new FormData();

      formData.append("video", videoFile);

      formData.append("minConfidence", minConfidence);

      return await api.post("/video/labels", formData);

    } catch (error) {

      console.error("Error starting label detection:", error);

      throw error;

    }

  },


  getLabelDetection: async (jobId) => {

    try {

      return await api.get(`/video/labels/${jobId}`);

    } catch (error) {

      console.error("Error getting label detection results:", error);

      throw error;

    }

  },


  startPersonTracking: async (videoFile) => {

    try {

      const formData = new FormData();

      formData.append("video", videoFile);

      return await api.post("/video/persons", formData);

    } catch (error) {

      console.error("Error starting person tracking:", error);

      throw error;

    }

  },


  getPersonTracking: async (jobId) => {

    try {

      return await api.get(`/video/persons/${jobId}`);

    } catch (error) {

      console.error("Error getting person tracking results:", error);

      throw error;

    }

  },

};


// Custom Labels API Calls

export const customLabelsAPI = {

  listProjects: async () => {

    try {

      return await api.get("/custom-labels/projects");

    } catch (error) {

      console.error("Error listing custom label projects:", error);

      throw error;

    }

  },


  listModelVersions: async (projectArn) => {

    try {

      return await api.get(`/custom-labels/models/${encodeURIComponent(projectArn)}`);

    } catch (error) {

      console.error("Error listing model versions:", error);

      throw error;

    }

  },


  detectCustomLabels: async (imageFile, projectVersionArn, minConfidence = 50) => {

    try {

      const formData = new FormData();

      formData.append("image", imageFile);

      formData.append("projectVersionArn", projectVersionArn);

      formData.append("minConfidence", minConfidence);

      return await api.post("/custom-labels/detect", formData);

    } catch (error) {

      console.error("Error detecting custom labels:", error);

      throw error;

    }

  },

  searchFaces: async (imageFile, collectionId, maxFaces = 5, faceMatchThreshold = 70) => {
    try {
      const formData = new FormData();
      formData.append("image", imageFile);
      formData.append("collectionId", collectionId);
      formData.append("maxFaces", maxFaces);
      formData.append("faceMatchThreshold", faceMatchThreshold);
      
      // Log request details for debugging
      console.log("Sending search request:", {
        collectionId, 
        maxFaces, 
        faceMatchThreshold,
        imageSize: Math.round(imageFile.size / 1024) + " KB"
      });
      
      return await api.post("/face/search", formData, {
        // Add timeout to prevent hanging requests
        timeout: 30000,
        // Ensure proper content type is set
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      console.error("Error details for face search:", error);
      throw error;
    }
  },
  
  listCollections: async () => {
    try {
      console.log("Requesting face collections list");
      return await api.get("/face/collections", {
        timeout: 10000
      });
    } catch (error) {
      console.error("Error listing collections:", error);
      throw error;
    }
  },  

};


export default {

  analyzeImage,

  faceAPI,

  videoAPI,

  customLabelsAPI,

  api

};
